import React from 'react';
import { createPortal } from 'react-dom';

/**
 * LoadingSpinner component
 * Displays a centered animated spinner and a status message to indicate loading state.
 *
 * Props:
 * - message (ReactNode, optional): The status message to display below the spinner. Defaults to 'Creating your sustainable journey…'.
 * - fullscreen (boolean, optional): If true, renders the spinner as a fullscreen overlay. Defaults to false.
 */
const LoadingSpinner: React.FC<{ message?: React.ReactNode; fullscreen?: boolean; children?: React.ReactNode }> = ({ message = 'Creating your sustainable journey…', fullscreen = false, children }) => {
  const spinnerContent = (
    <>
      {/* Animated spinner */}
      <div className="spinner" aria-label="Loading" />
      {/* Status message */}
      <div style={{ marginTop: 16, fontSize: 16, color: '#555' }}>{message}</div>
      {children && (
        <div style={{ marginTop: 16 }}>
          {children}
        </div>
      )}
      {/* Inline CSS for spinner animation */}
      <style>
        {`
          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #eee;
            border-top: 4px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );

  if (fullscreen) {
    const overlay = (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        // Increased opacity for better visibility
        background: 'rgba(255,255,255,0.9)', zIndex: 9999,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        {spinnerContent}
      </div>
    );

    if (typeof document !== 'undefined' && document.body) {
      return createPortal(overlay, document.body);
    }

    return overlay;
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: 200, width: '100%'
    }}>
      {spinnerContent}
    </div>
  );
};

export default LoadingSpinner; 